import { AnimatePresence, LazyMotion, domAnimation, m } from 'framer-motion';
import { X } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import type { DashboardNavGroup, DashboardNavItem } from '../navigation';
import type { DashboardTranslations } from '../translations';

interface MobileNavigationProps {
  groups: DashboardNavGroup[];
  quickItems: DashboardNavItem[];
  drawerOpen: boolean;
  onClose: () => void;
  t: DashboardTranslations;
}

export function MobileNavigation({ groups, quickItems, drawerOpen, onClose, t }: MobileNavigationProps) {
  return (
    <LazyMotion features={domAnimation} strict>
      <nav
        className="fixed inset-x-3 bottom-3 z-40 grid grid-cols-4 gap-2 rounded-2xl bg-surface-card border border-surface-border p-2 shadow-lift backdrop-blur-2xl lg:hidden"
        aria-label={t.mobileNav.label}
      >
        {quickItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.to === '/dashboard'}
              className={({ isActive }) =>
                `flex min-h-14 flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-semibold transition-colors ${
                  isActive
                    ? 'bg-accent/10 text-accent'
                    : 'text-brand-muted hover:bg-surface-hover hover:text-brand-white'
                }`
              }
            >
              <Icon size={18} />
              <span className="max-w-full truncate px-1">{t.nav.items[item.key]}</span>
            </NavLink>
          );
        })}
      </nav>

      <AnimatePresence>
        {drawerOpen ? (
          <m.div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label={t.mobileNav.drawerLabel}>
            <m.button
              type="button"
              className="absolute inset-0 bg-black/60 backdrop-blur-md"
              onClick={onClose}
              aria-label={t.mobileNav.closeMenu}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            <m.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 400, damping: 38 }}
              className="relative h-full w-[min(88vw,360px)] overflow-y-auto border-e border-surface-border bg-surface-base px-4 py-6 shadow-lift"
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-widest text-accent mb-1 block">
                    {t.pageEyebrow}
                  </span>
                  <h2 className="font-display text-display-md text-brand-white">{t.pageTitle}</h2>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex size-10 items-center justify-center rounded-lg border border-surface-border text-brand-muted hover:border-accent/30 hover:text-brand-white transition-colors"
                  aria-label={t.mobileNav.closeMenu}
                >
                  <X size={18} />
                </button>
              </div>
              <nav className="mt-8 space-y-4" aria-label={t.mobileNav.drawerLabel}>
                {groups.map((group) => (
                  <section key={group.key} className="rounded-2xl bg-surface-card/60 border border-surface-border p-3">
                    <h3 className="px-2 text-xs font-semibold uppercase tracking-widest text-accent">
                      {t.nav.groups[group.key]}
                    </h3>
                    <div className="mt-3 space-y-1">
                      {group.items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <NavLink
                            key={item.key}
                            to={item.to}
                            end={item.to === '/dashboard'}
                            onClick={onClose}
                            className={({ isActive }) =>
                              `flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors ${
                                isActive
                                  ? 'bg-accent text-surface-base'
                                  : 'text-brand-muted hover:bg-surface-hover hover:text-brand-white'
                              }`
                            }
                          >
                            <Icon size={16} />
                            <span>{t.nav.items[item.key]}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </nav>
            </m.aside>
          </m.div>
        ) : null}
      </AnimatePresence>
    </LazyMotion>
  );
}
